import { getConfig } from 'EnvConfig'

import { primePublicPortalConfig } from './bootstrap/fetch-public-portal-config'
import {
  dismissEntryTeaser,
  paintEntryTeaser,
  readCognitoIdToken,
  readEntryTeaserUrl,
  waitForBootTeaser
} from './bootstrap/paint-entry-teaser'
import {
  BOOT_SOURCE_TIMEOUT_MS,
  isBootAppearanceDark,
  isLoginDocument,
  preloadLoginLogo,
  readLoginHeaderLogo,
  readLoginPageLogo,
  readLoginThemeLogo,
  waitForBootSource
} from './bootstrap/paint-login-logo'
import { primePortalPages } from './bootstrap/prime-portal-pages'
import { requestPublicPortalPages } from './bootstrap/prime-public-pages'
import { primeThemeData } from './bootstrap/prime-theme-data'
import { warmSessionRoot } from './bootstrap/warm-session-root'

const preconnect = (href: string) => {
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
    return
  }

  const link = document.createElement('link')

  link.rel = 'preconnect'
  link.href = href
  document.head.appendChild(link)
}

const loadApp = () => ({
  i18nReady: import('./i18n/config').then((module) => module.i18nReady),
  appModulePromise: import('index')
})

// Closed once the app code is ready, so a late teaser never covers the app
type BootPaint = { isOpen: boolean }

const paintEntryBoot = async (
  idToken: string,
  portalConfigPromise: ReturnType<typeof primePublicPortalConfig>,
  pathname: string,
  paint: BootPaint
) => {
  const pages = await waitForBootSource(
    portalConfigPromise.then((config) => primePortalPages(idToken, config))
  )
  const teaserUrl = pages ? readEntryTeaserUrl(pages, pathname) : undefined

  if (!teaserUrl || !paint.isOpen) return

  paintEntryTeaser(teaserUrl)
  await waitForBootTeaser()
}

const continueLoginLogoPreload = async (
  publicPagesPromise: ReturnType<typeof requestPublicPortalPages> | undefined,
  config: Record<string, unknown>,
  pathname: string,
  themePromise: Promise<unknown> | undefined
) => {
  const isDark = isBootAppearanceDark()
  const theme = await waitForBootSource(themePromise)
  const themeLogo = readLoginThemeLogo(theme, isDark)

  if (themeLogo) {
    preloadLoginLogo(themeLogo)

    return
  }

  const pages = await waitForBootSource(publicPagesPromise)
  const pageLogo = readLoginPageLogo(pages, pathname, isDark)
  const url = pageLogo || readLoginHeaderLogo(config)

  if (url) preloadLoginLogo(url)
}

const bootstrap = async () => {
  if (window.bootstrapped) return

  window.bootstrapped = true

  const { CUSTOMER_PORTAL_API_URL, DESIGN_BUILDER_API_URL } = getConfig()

  preconnect(new URL(CUSTOMER_PORTAL_API_URL).origin)
  preconnect(new URL(DESIGN_BUILDER_API_URL).origin)

  const portalConfigPromise = primePublicPortalConfig()
  const pathname = window.location.pathname
  const isLogin = isLoginDocument(pathname)
  const idToken = isLogin ? undefined : readCognitoIdToken()
  const app = loadApp()
  warmSessionRoot()
  const publicPagesPromise = isLogin ? requestPublicPortalPages() : undefined

  // The teaser only gets the head start the app code needs to download anyway
  if (idToken) {
    const paint: BootPaint = { isOpen: true }

    await Promise.race([
      paintEntryBoot(idToken, portalConfigPromise, pathname, paint).catch(
        () => undefined
      ),
      Promise.all([app.i18nReady, app.appModulePromise]).catch(() => undefined)
    ])
    paint.isOpen = false
  }

  const portalConfig = await portalConfigPromise

  const isEmbedded = window.parent !== window
  let isDummyOrConfigMode = false

  if (isEmbedded) {
    const [{ getDummyApi }, { initPreview }] = await Promise.all([
      import('dummy/static'),
      import('preview')
    ])

    isDummyOrConfigMode = initPreview(portalConfig)

    if (isDummyOrConfigMode) {
      const Dummy = await getDummyApi()

      await Dummy?.getConfig(portalConfig as never)
    }
  }

  const { organization_id, design_id } = portalConfig
  const loginHeaderLogo = portalConfig.images?.loginHeaderLogo
  const themePromise =
    !isDummyOrConfigMode && organization_id && design_id
      ? primeThemeData(organization_id, design_id, {
          fallbackLogoUrl:
            typeof loginHeaderLogo === 'string' ? loginHeaderLogo : undefined
        })
      : undefined

  if (isLogin && !isDummyOrConfigMode) {
    void continueLoginLogoPreload(
      publicPagesPromise,
      portalConfig,
      pathname,
      themePromise
    ).catch(() => undefined)
  }

  if (portalConfig.prevent_search_engine_indexing) {
    let metaRobots = document.head.querySelector('meta[name="robots"]')

    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.setAttribute('name', 'robots')
      document.head.appendChild(metaRobots)
    }

    metaRobots.setAttribute('content', 'noindex, nofollow')
  }

  await app.i18nReady

  const { main } = await app.appModulePromise

  main()

  // Last resort when no teaser block or navigation removed it (render error, page without a teaser)
  window.setTimeout(dismissEntryTeaser, BOOT_SOURCE_TIMEOUT_MS)
}

export default bootstrap
